import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetLocationByIdQuery } from '../api/locations/locations.api';
import { useLazyGetStationByIdQuery } from '../api/station/station.api';
import {
    acquireSocketConnection,
    releaseSocketConnection,
    joinStations,
    leaveStations,
    onStationUpdated,
} from '../utils/socket-io';
import { POLLING_INTERVAL_MS, seedStationsCache, upsertStationInCaches } from '../utils/station-cache';

export const useLocationSocket = (locationId: string | undefined, skipSocket = false) => {
    const dispatch = useDispatch();
    const joinedIdentitiesRef = useRef<string[]>([]);
    const [fetchStation] = useLazyGetStationByIdQuery();

    const {
        data: location,
        isLoading: locationLoading,
        error: locationError,
        refetch: refetchLocation,
    } = useGetLocationByIdQuery(locationId ?? '', {
        skip: !locationId,
        refetchOnMountOrArgChange: true,
        pollingInterval: POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
    });

    const stationIdentitiesKey = useMemo(
        () => location?.data?.stations?.map(s => s.identity).sort().join(',') ?? '',
        [location?.data?.stations]
    );

    const identityToId = useMemo(() => {
        const map = new Map<string, string>();
        location?.data?.stations?.forEach(station => {
            map.set(station.identity, station._id);
        });
        return map;
    }, [stationIdentitiesKey, location?.data?.stations]);

    useEffect(() => {
        if (!location?.data?.stations?.length) return;
        seedStationsCache(dispatch, location.data.stations);
    }, [stationIdentitiesKey, location?.data?.stations, dispatch]);

    useEffect(() => {
        if (!locationId || skipSocket || !stationIdentitiesKey) return;

        const identities = stationIdentitiesKey.split(',');
        joinedIdentitiesRef.current = identities;

        acquireSocketConnection();
        joinStations(identities);

        const unsubscribe = onStationUpdated(async identity => {
            const stationId = identityToId.get(identity);
            if (!stationId) return;

            try {
                const response = await fetchStation(stationId).unwrap();
                upsertStationInCaches(dispatch, locationId, stationId, response);
            } catch {
                // polling will retry
            }
        });

        return () => {
            unsubscribe();
            leaveStations(joinedIdentitiesRef.current);
            releaseSocketConnection();
        };
    }, [locationId, skipSocket, stationIdentitiesKey, identityToId, dispatch, fetchStation]);

    return { location, locationLoading, locationError, refetchLocation };
};
